import styled from 'styled-components';

import { useContent } from '../content/load';
import { useDocumentTitle } from '../lib/seo';
import { ArticleRow } from '../components/blocks/ArticleRow';
import { FeatureArticle } from '../components/blocks/FeatureArticle';
import { SectionRenderer } from '../components/blocks/SectionRenderer';
import { StrategyCard } from '../components/blocks/StrategyCard';
import { EquityCurve } from '../components/charts/EquityCurve';
import { Container } from '../components/primitives/Container';
import { SectionHead } from '../components/primitives/SectionHead';
import { equityCurve } from '../lib/equity-curve';

const Hero = styled.section`
  padding: ${(p) => p.theme.step[8]}px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: ${(p) => p.theme.step[7]}px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Strategies = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${(p) => p.theme.step[5]}px;
`;

const ArticleSplit = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: ${(p) => p.theme.step[7]}px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export default function Home() {
  const { strategies, articles, pages } = useContent();
  const homePage = pages.home;
  useDocumentTitle(homePage?.seo.title, homePage?.seo.description);
  const heroData = equityCurve({ seed: 42, steps: 240 });
  const hero = articles.find((a) => a.hero);
  const recent = articles.filter((a) => !a.hero).slice(0, 6);

  return (
    <>
      {homePage ? (
        homePage.sections
          .filter((s) => s.kind === 'hero')
          .map((s, i) => <SectionRenderer key={`hero-${i}`} section={s} />)
      ) : (
        <Hero>
          <Container>
            <Grid>
              <div>
                <h1
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: 'clamp(44px, 5vw, 84px)',
                    margin: '0 0 16px',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.05,
                  }}
                >
                  Systematic, transparent, accountable.
                </h1>
                <p style={{ fontSize: 18, color: 'var(--text-1)', maxWidth: 520 }}>
                  PowerQuant builds systematic trading strategies you can audit, subscribe to, and
                  hold accountable.
                </p>
              </div>
              <EquityCurve data={heroData} height={320} />
            </Grid>
          </Container>
        </Hero>
      )}

      {homePage?.sections
        .filter((s) => s.kind !== 'hero')
        .map((s, i) => <SectionRenderer key={`s-${i}`} section={s} />)}

      <section>
        <Container>
          <SectionHead
            eyebrow="STRATEGIES"
            heading="A book you can read."
            body="Each strategy ships with its thesis, mechanics, and a live equity curve."
          />
          <Strategies>
            {strategies.slice(0, 6).map((s) => (
              <StrategyCard key={s.id} strategy={s} />
            ))}
          </Strategies>
        </Container>
      </section>

      <section>
        <Container>
          <SectionHead eyebrow="RESEARCH" heading="Recent writing." />
          <ArticleSplit>
            {hero && <FeatureArticle article={hero} />}
            <div>
              {recent.map((a) => (
                <ArticleRow key={a.id} article={a} />
              ))}
            </div>
          </ArticleSplit>
        </Container>
      </section>
    </>
  );
}
